import { useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { StockCatalogueItem } from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { FieldShell } from "./StockFormField";

interface CatalogueItemPickerProps {
  id: string;
  label?: string;
  value: StockCatalogueItem | null;
  onChange: (item: StockCatalogueItem | null) => void;
  error?: string;
  hint?: string;
}

/** Search every catalogue (standard and customer) for the one repeatable
 * catalogue item a stock item is linked to. */
export function CatalogueItemPicker({
  id,
  label = "Repeatable catalogue item (optional)",
  value,
  onChange,
  error,
  hint = "The repeatable catalogue item this stock is reprinted from; it gives the reprint spec and standing artwork. Leave empty for packaging, merchandise and consumables.",
}: CatalogueItemPickerProps) {
  const dataAdapter = useDataAdapter();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);
  const [results, setResults] = useState<StockCatalogueItem[]>([]);
  const [searchFailed, setSearchFailed] = useState(false);

  useEffect(() => {
    if (value || debouncedQuery.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearchFailed(false);
    dataAdapter
      .searchStockCatalogueItems(debouncedQuery.trim())
      .then((found) => !cancelled && setResults(found))
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error("stock.catalogue_items.search_failed", {
          status: err instanceof ApiError ? err.status : undefined,
        });
        setSearchFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, debouncedQuery, value]);

  return (
    <FieldShell
      id={id}
      label={label}
      error={error}
      hint={value ? undefined : hint}
    >
      {(aria) =>
        value ? (
          <div className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm">
            <span>
              <span className="font-medium">{value.name}</span>
              {value.catalogue_name && (
                <span className="text-muted-foreground">
                  {" "}
                  · {value.catalogue_name}
                </span>
              )}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                onChange(null);
                setQuery("");
              }}
            >
              {value.catalogue_name ? "Change" : "Remove"}
            </Button>
          </div>
        ) : (
          <div className="space-y-1">
            <Input
              id={id}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. brochure or Hepco Rail"
              autoComplete="off"
              {...aria}
            />
            {searchFailed && (
              <p className="text-sm text-red-600 dark:text-red-400">
                Couldn't search the catalogue. Please try again.
              </p>
            )}
            {results.length > 0 && (
              <ul
                aria-label="Matching catalogue items"
                className="rounded-md border border-border divide-y divide-border max-h-48 overflow-y-auto"
              >
                {results.map((item) => (
                  <li key={item.item_id}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                      onClick={() => onChange(item)}
                    >
                      <span className="font-medium">{item.name}</span>
                      <span className="text-muted-foreground">
                        {" "}
                        · {item.catalogue_name}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!searchFailed &&
              debouncedQuery.trim().length >= 2 &&
              results.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No catalogue items match.
                </p>
              )}
          </div>
        )
      }
    </FieldShell>
  );
}

/** The picker's value for a stored link: the item, or a placeholder naming
 * a link whose catalogue item has since been removed. */
export function linkedCatalogueValue(
  catalogueItemId: string | null | undefined,
  catalogueItem: StockCatalogueItem | null | undefined,
): StockCatalogueItem | null {
  if (catalogueItem) return catalogueItem;
  if (!catalogueItemId) return null;
  return {
    item_id: catalogueItemId,
    name: "No longer in the catalogue",
    product_label: "",
    catalogue_name: "",
  };
}
